import { useAuthStore } from '@/auth/store';
import { servesPortal } from '@/lib/portalHost';
import { useCurrentPatient, useCurrentUser } from './queries';

export type PortalRole = 'clinician' | 'patient';

const isPortalRole = (v: unknown): v is PortalRole => v === 'clinician' || v === 'patient';

/**
 * Home route for a resolved role. Anything that is not a known role, or whose
 * portal this host does not serve (see servesPortal), has NO home and goes to
 * /forbidden — never defaulted into the clinician area. Used by both
 * RoleRoute's cross-role redirect and the root redirect, so the two can't
 * disagree or loop.
 */
export const roleHome = (
  role: PortalRole | undefined,
  hostname: string = window.location.hostname,
): string => {
  if (!role || !servesPortal(role, hostname)) return '/forbidden';
  return role === 'patient' ? '/me' : '/patients';
};

export type ResolvedRoles = {
  /**
   * Every portal this user may enter. A single `DBUser` can hold both a
   * clinician and a patient row — staff who are also enrolled — which MNT
   * supports deliberately.
   */
  roles: PortalRole[];
  /**
   * Where they land by default, and the target of a cross-role redirect.
   * Clinician wins for a dual-role user: staff sign in to do their job.
   * `undefined` when there is no role to route to.
   */
  role: PortalRole | undefined;
  isLoading: boolean;
  isError: boolean;
};

const NONE: PortalRole[] = [];

/**
 * Resolve which portals the signed-in user may enter, from the server and
 * never from token shape (OAuth scope only encodes admin-vs-user).
 *
 * Three sources, in order of authority:
 *
 *   1. `is_clinician` / `is_patient` on `/users/current` — the only signal
 *      that can express BOTH. This is what makes a dual-role user work.
 *   2. `role` — one winner. Correct for single-role users, and still the
 *      primary for routing, but on its own it hides the other half of a
 *      dual-role user and locks them out of that portal. [FOOD-1134]
 *   3. Probing `/patients/current`: 200 ⇒ holds a patient row, 400 ⇒ does
 *      not. Legacy fallback for a deployment predating the fields above.
 *
 * Fails closed throughout: an empty set, an error, or an unrecognised value
 * resolves to no role, which RoleRoute sends to /forbidden. Ambiguity never
 * widens access.
 */
export const useResolvedRole = (): ResolvedRoles => {
  const token = useAuthStore((s) => s.token);
  const user = useCurrentUser({ enabled: !!token, retry: false });
  const data = user.data;

  // Presence of the FIELD, not its truthiness — an explicit `false` is an
  // answer and must not fall through to the probe.
  const hasRoleFlags =
    user.isSuccess && !!data && ('is_clinician' in data || 'is_patient' in data);
  // Same reasoning for `role`: an explicit null means "neither", and must
  // short-circuit rather than get misclassified by the probe.
  const hasRoleField = user.isSuccess && !!data && 'role' in data;

  const needProbe = !!token && user.isSuccess && !hasRoleFlags && !hasRoleField;
  const patient = useCurrentPatient({ enabled: needProbe, retry: false });

  if (!token) return { roles: NONE, role: undefined, isLoading: false, isError: false };
  if (user.isLoading) return { roles: NONE, role: undefined, isLoading: true, isError: false };
  if (user.isError) return { roles: NONE, role: undefined, isLoading: false, isError: true };

  if (hasRoleFlags) {
    const roles: PortalRole[] = [];
    if (data?.is_clinician === true) roles.push('clinician');
    if (data?.is_patient === true) roles.push('patient');
    return { roles, role: roles[0], isLoading: false, isError: false };
  }

  if (hasRoleField) {
    const explicit = isPortalRole(data?.role) ? data.role : undefined;
    return {
      roles: explicit ? [explicit] : NONE,
      role: explicit,
      isLoading: false,
      isError: false,
    };
  }

  // The probe can only ever prove "is a patient". It says nothing about
  // whether they are ALSO a clinician, so a 200 must not be read as
  // "patient only" — that would redirect dual-role staff out of the
  // clinician portal, which is the worse of the two failures here.
  //
  // `/users/current` carries a `clinician` block for anyone holding a
  // clinician row, so pair that with the probe instead of letting either
  // decide alone. Both are positive server assertions, so combining them
  // does not widen on ambiguity — absence of both still yields no role.
  if (patient.isSuccess) {
    const roles: PortalRole[] = data?.clinician
      ? ['clinician', 'patient']
      : ['patient'];
    return { roles, role: roles[0], isLoading: false, isError: false };
  }
  if (patient.isError) {
    const status = (patient.error as { response?: { status?: number } } | null)?.response?.status;
    // 400 = "current user is not a patient" ⇒ clinician. Anything else is a
    // genuine failure we must not mistake for a role.
    if (status === 400) {
      return { roles: ['clinician'], role: 'clinician', isLoading: false, isError: false };
    }
    return { roles: NONE, role: undefined, isLoading: false, isError: true };
  }
  return { roles: NONE, role: undefined, isLoading: true, isError: false };
};
